import React, {createContext, useEffect, useState, useRef, useCallback} from 'react';
import {getConfig, PRISMAX_BACKEND_URL, isBetaEnvironment, isLocalDevelopment} from "../configs/config";
import FirstTimeModal from "./FirstTimeModal";
import {fetchEVMTokenBalances, getEvmChainConfig, isEvmChain} from '../components/header/ConnectWalletHeaderOnchainHelper';
import {useRecaptcha} from "../utils/useRecaptcha";
import {
    ADMIN_SESSION_EXPIRED_EVENT,
    ADMIN_SESSION_EXPIRED_MESSAGE,
    USER_SESSION_EXPIRED_EVENT,
    USER_SESSION_EXPIRED_MESSAGE,
} from '../utils/authErrors';
import {getLinkedWalletChainFromUserData} from '../utils/walletChain';
import {getAccountRoleFromUserData, getQaRoleFromUserData} from '../utils/userRoles';

function getEVMProvider() {
    if (window.ethereum) {
        return window.ethereum;
    }
    if (window.okxwallet) {
        return window.okxwallet;
    }
    if (window.coinbaseWalletProvider) {
        return window.coinbaseWalletProvider;
    }
    return null;
}

export const SwapMainAppContext = createContext(null);

export function SwapMainAppProvider({children}) {
    const {isRecaptchaReady, executeRecaptchaAction} = useRecaptcha();
    const [isContextReady, setIsContextReady] = useState(false);
    const [notification, setNotification] = useState({
        status: null,
        message: null,
    });
    const notificationTimerRef = useRef(null);
    const [shrinkLeftPanel, setShrinkLeftPanel] = useState(false);
    //connect panel
    const [showPanel, setShowPanel] = useState(false);

    // Add transaction-related state
    const [userClass, setUserClass] = useState(() => {
        return localStorage.getItem('userClass') || 'Explorer Member';
    });
    const [userRole, setUserRole] = useState(() => {
        return localStorage.getItem('userRole') || '';
    });
    const [qaRole, setQaRole] = useState(() => {
        return localStorage.getItem('qaRole') || '';
    });
    const [showFirstTimeModal, setShowFirstTimeModal] = useState(false);
    const [userSettings, setUserSettings] = useState({});

    const [selectedToken, setSelectedToken] = useState('USDT');
    const [amount, setAmount] = useState('');
    const [transactionTrigger, setTransactionTrigger] = useState(0);
    const [connectWalletModalRequest, setConnectWalletModalRequest] = useState(0);
    const [connectWalletModalForceWalletList, setConnectWalletModalForceWalletList] = useState(false);
    const [onMembershipPaymentSuccess, setOnMembershipPaymentSuccess] = useState(() => () => {
        console.log("onMembershipPaymentSuccess default no-op called");
    });
    const [isProcessing, setIsProcessing] = useState(false);

    // Used by DataQAReview to guard navigation (side menu, mobile menu) while a QA review batch is in progress
    const [isQaReviewInProgress, setIsQaReviewInProgress] = useState(false);
    const [requestLeaveQaReviewConfirmation, setRequestLeaveQaReviewConfirmation] = useState(
        () => (onConfirm) => onConfirm?.()
    );

    const [usdtBalance, setUsdtBalance] = useState(0);
    const [usdcBalance, setUsdcBalance] = useState(0);
    const [gatewayToken, setGatewayToken] = useState(
        localStorage.getItem('gatewayToken') || ''
    );
    const [sessionUrlKey, setSessionUrlKey] = useState('')
    const [pointsRefreshTrigger, setPointsRefreshTrigger] = useState(0);

    const [usdtTokenAddress, setUsdtTokenAddress] = useState('');
    const [usdcTokenAddress, setUsdcTokenAddress] = useState('');
    const [prismaxRecevingAddress, setPrismaxRecevingAddress] = useState('');
    const [prismaxEvmReceivingAddress, setPrismaxEvmReceivingAddress] = useState('');

    // Robot state
    const [livePaused, setLivePaused] = useState({});
    const [videoId, setVideoId] = useState({});
    const [robotDataDict, setRobotDataDict] = useState({}); // Dictionary of robotId to { robotName, robotClass, robotSlug }
    const [robotSlugToRobotId, setRobotSlugToRobotId] = useState({}); 
    const [globalLiveRobotId, setGlobalLiveRobotId] = useState('');

    const [isQueueAttentionActive, setIsQueueAttentionActive] = useState(false);
    const defaultFaviconRef = useRef(null);

    const [walletAddress, setWalletAddress] = useState(() => {
        return localStorage.getItem('walletAddress') || '';
    });
    const [linkedWalletChain, setLinkedWalletChain] = useState(() => {
        return localStorage.getItem('linkedWalletChain') || '';
    });
    const [userEmail, setUserEmail] = useState(() => {
        return localStorage.getItem('userEmail') || '';
    });
    const [userId, setUserId] = useState(() => {
        const storedUserId = localStorage.getItem('userId');
        return storedUserId ? parseInt(storedUserId, 10) : null;
    });
    const [referralCode, setReferralCode] = useState('');

    // VLA data cache
    const [shouldRefreshVLADataCache, setShouldRefreshVLADataCache] = useState(false);

    // Multi-chain support
    const [selectedChain, setSelectedChain] = useState(() => {
        return localStorage.getItem('selectedChain') || 'solana';
    });

    // for error message
    function showNotification(status, message, showTimed = 5000) {
        if (notificationTimerRef.current) {
            clearTimeout(notificationTimerRef.current);
            notificationTimerRef.current = null;
        }

        setNotification({status, message});

        notificationTimerRef.current = setTimeout(() => {
            setNotification({status: null, message: null});
            notificationTimerRef.current = null;
        }, showTimed);
    }

    const requestConnectWalletModal = useCallback(({forceWalletListView = true} = {}) => {
        setConnectWalletModalForceWalletList(Boolean(forceWalletListView));
        setConnectWalletModalRequest((current) => current + 1);
    }, []);


    useEffect(() => {
        return () => {
            if (notificationTimerRef.current) {
                clearTimeout(notificationTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const initializeContext = async () => {
            try {
                const configData = await getConfig();
                setUsdtTokenAddress(configData.usdtTokenAddress || '');
                setUsdcTokenAddress(configData.usdcTokenAddress || '');
                setPrismaxRecevingAddress(configData.prismaxRecevingAddress || '');
                setPrismaxEvmReceivingAddress(configData.prismaxEvmReceivingAddress || '');

                if (configData.robotStatus && configData.robotStatus.robots) {
                    const videoIdDict = {};
                    const livePausedDict = {};
                    const robotDataResDict = {};
                    const robotSlugToRobotIdDict = {};
                    configData.robotStatus.robots.forEach(status => {
                        videoIdDict[status.robot_id] = status.live_paused
                            ? ''
                            : (status.youtube_stream_id || '');

                        livePausedDict[status.robot_id] =
                            isBetaEnvironment || isLocalDevelopment
                                ? false
                                : !!status.live_paused;
                        // livePausedDict[status.robot_id] =  !!status.live_paused;
                        robotDataResDict[status.robot_id] = {
                            robotClass: status.robot_class ?? '',
                            robotName: status.robot_name ?? 'Robotic Arm Control',
                            robotSlug: status.robot_slug ?? ''
                        };
                        robotSlugToRobotIdDict[status.robot_slug] = status.robot_id;
                    });

                    const globalLiveRobotIdData = configData.robotStatus.robots.find(
                        (status) => status.is_global_live
                    )?.robot_id ?? 'arm1';

                    setVideoId(videoIdDict);
                    setLivePaused(livePausedDict);
                    setRobotDataDict(robotDataResDict);
                    setRobotSlugToRobotId(robotSlugToRobotIdDict);
                    setGlobalLiveRobotId(globalLiveRobotIdData);
                }

                if (userEmail) {
                    await fetchUserInfoAfterConnect();
                }

                setIsContextReady(true);
            } catch (err) {
                console.error('Error initializing context:', err);
                setIsContextReady(true);
            }
        };

        initializeContext();
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (!defaultFaviconRef.current) {
            const link = document.querySelector('link[rel="icon"]');
            defaultFaviconRef.current = (link && link.getAttribute('href')) || '/favicon.svg';
        }
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const nextHref = isQueueAttentionActive
            ? '/favicon-alert.svg'
            : (defaultFaviconRef.current || '/favicon.svg');
        const selectors = ['link[rel="icon"]', 'link[rel="shortcut icon"]'];
        selectors.forEach((selector) => {
            document.querySelectorAll(selector).forEach((link) => {
                if (link.getAttribute('href') !== nextHref) {
                    link.setAttribute('href', nextHref);
                }
            });
        });
    }, [isQueueAttentionActive]);

    // Function to fetch EVM balances
    const fetchEVMBalances = async () => {
        if (!walletAddress || !isEvmChain(selectedChain)) {
            return;
        }

        try {
            const provider = getEVMProvider();
            if (provider) {
                const balances = await fetchEVMTokenBalances(provider, walletAddress, selectedChain);
                setUsdcBalance(balances.usdcBalance || 0);

                const supportsUSDT = !!getEvmChainConfig(selectedChain)?.supportedTokens?.USDT?.address;
                setUsdtBalance(supportsUSDT ? (balances.usdtBalance || 0) : 0);
            }
        } catch (error) {
            console.error('Error fetching EVM balances:', error);
            setUsdcBalance(0);
            setUsdtBalance(0);
        }
    };

    const fetchUserInfoAfterConnect = async () => {
        try {
            // Wait for reCAPTCHA to be ready before making the request
            if (!isRecaptchaReady) {
                console.log('Waiting for reCAPTCHA to be ready before fetching user info');
                return;
            }

            // Generate reCAPTCHA token
            const recaptchaToken = await executeRecaptchaAction('fetch_user_info');

            let url = `${PRISMAX_BACKEND_URL}/api/get-users`;
            let headers = {
                'Content-Type': 'application/json'
            };

            if (userEmail) {
                if (!gatewayToken) {
                    console.error('No token found for email authentication');
                    return;
                }

                const params = new URLSearchParams();
                params.append('email', userEmail);
                params.append('chain', selectedChain);
                params.append('recaptcha_token', recaptchaToken);
                url += `?${params}`;

                headers['Authorization'] = `Bearer ${gatewayToken}`;
                // todo could be issue here
                // } else if (walletAddress) {
                //     const params = new URLSearchParams();
                //     params.append('wallet_address', walletAddress);
                //     url += `?${params}`;
                // }
            } else {
                console.error('No email  available');
                return;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            if (response.ok) {
                const result = await response.json();

                if (result.success && result.data) {
                    if (result.data.user_class) {
                        localStorage.setItem('userClass', result.data.user_class);
                        setUserClass(result.data.user_class);
                    }
                    const nextUserRole = getAccountRoleFromUserData(result.data);
                    const nextQaRole = getQaRoleFromUserData(result.data);
                    setUserRole(nextUserRole);
                    setQaRole(nextQaRole);
                    if (nextUserRole) localStorage.setItem('userRole', nextUserRole);
                    else localStorage.removeItem('userRole');
                    if (nextQaRole) localStorage.setItem('qaRole', nextQaRole);
                    else localStorage.removeItem('qaRole');
                    if (result.data.hash_code) {
                        localStorage.setItem('gatewayToken', result.data.hash_code);
                        setGatewayToken(result.data.hash_code)
                    }
                    if (result.data.userid) {
                        localStorage.setItem('userId', result.data.userid);
                        setUserId(result.data.userid)
                    }
                    if (result.data.referral_code) {
                        setReferralCode(result.data.referral_code);
                    }
                    if (result.data.settings) {
                        setUserSettings(result.data.settings);
                    }
                    if (result.data.linked_wallet_address) {
                        const nextLinkedWalletChain = getLinkedWalletChainFromUserData(result.data);
                        localStorage.setItem('walletAddress', result.data.linked_wallet_address);
                        setWalletAddress(result.data.linked_wallet_address);

                        if (nextLinkedWalletChain) {
                            localStorage.setItem('linkedWalletChain', nextLinkedWalletChain);
                            localStorage.setItem('selectedChain', nextLinkedWalletChain);
                            setLinkedWalletChain(nextLinkedWalletChain);
                            setSelectedChain(nextLinkedWalletChain);
                        }
                    } else {
                        localStorage.removeItem('linkedWalletChain');
                        setLinkedWalletChain('');
                    }
                    if (result.wallet_member && result.wallet_member !== 'Explorer Member') {
                        showNotification('warning', 'The wallet has been registered please use a different wallet or login directly with your wallet. Unlink your email from your wallet in the Account page.', 15000)
                    }
                }
            } else {
                const errorData = await response.json();
                console.error('Failed to fetch user info:', errorData.msg || 'Unknown error');
            }
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    };

    const handleDailyLoginPoints = async () => {
        try {
            const requestBody = {};
            if (userEmail) {
                requestBody.email = userEmail;
            } else if (walletAddress) {
                requestBody.wallet_address = walletAddress;
                requestBody.chain = selectedChain;
            } else {
                return;
            }
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');

            requestBody.user_local_date = `${year}-${month}-${day}`;
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/daily-login-points`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data.is_first_time) {
                    setShowFirstTimeModal(true);
                    setPointsRefreshTrigger(prev => prev + 1);
                } else if (data.success && data.data.points_awarded_today > 0) {
                    showNotification('success', `Welcome! You earned ${data.data.points_awarded_today} points today!`);
                    setPointsRefreshTrigger(prev => prev + 1);
                }
            }
        } catch (error) {
            console.error('Error handling daily login points:', error);
        }
    };

    useEffect(() => {
        // Only proceed if reCAPTCHA is ready
        if (!isRecaptchaReady) {
            return;
        }

        if (userEmail) {
            fetchUserInfoAfterConnect();
            handleDailyLoginPoints();
        } else if (walletAddress) {
            handleDailyLoginPoints();
        }

        // Fetch EVM balances when wallet address changes
        if (walletAddress && isEvmChain(selectedChain)) {
            fetchEVMBalances();
        }
    }, [walletAddress, userEmail, isRecaptchaReady]);

    // Fetch balances when chain changes
    useEffect(() => {
        if (walletAddress && isEvmChain(selectedChain)) {
            fetchEVMBalances();
        } else if (selectedChain === 'solana') {
            // Reset EVM balances when switching to Solana
            setUsdcBalance(0);
            setUsdtBalance(0);
        }
    }, [selectedChain]);

    useEffect(() => {
        if (walletAddress) {
            localStorage.setItem('walletAddress', walletAddress);
        } else {
            localStorage.removeItem('walletAddress');
        }
    }, [walletAddress]);

    useEffect(() => {
        if (userEmail) {
            localStorage.setItem('userEmail', userEmail);
        } else {
            localStorage.removeItem('userEmail');
        }
    }, [userEmail]);

    // Persist selectedChain to localStorage
    useEffect(() => {
        if (selectedChain) {
            localStorage.setItem('selectedChain', selectedChain);
        }
    }, [selectedChain]);

    useEffect(() => {
        if (linkedWalletChain) {
            localStorage.setItem('linkedWalletChain', linkedWalletChain);
        } else {
            localStorage.removeItem('linkedWalletChain');
        }
    }, [linkedWalletChain]);

    const clearUserData = useCallback(() => {
        //  reset in-memory state
        setWalletAddress('');
        setUserEmail('');
        setUserClass('Explorer Member');
        setUserRole('');
        setQaRole('');
        setUsdtBalance(0);
        setUsdcBalance(0);
        setGatewayToken('');
        setSessionUrlKey('');
        setAmount('');
        setTransactionTrigger(0);
        setIsProcessing(false);
        setUserId(null);
        setReferralCode('');
        setLinkedWalletChain('');
        console.log('user data cleared')
        // Don't reset selectedChain when clearing user data 

    }, []);

    const clearLocalSessionData = useCallback(() => {
        const betaAccessToken = localStorage.getItem('betaAccessToken');
        const currentChain = selectedChain;

        localStorage.clear();
        if (betaAccessToken) {
            localStorage.setItem('betaAccessToken', betaAccessToken);
        }
        if (currentChain) {
            localStorage.setItem('selectedChain', currentChain);
        }
        localStorage.setItem('emailLogin', 'false');

        clearUserData();
        setSelectedChain(currentChain);
    }, [clearUserData, selectedChain]);

    const handleUserSessionExpired = useCallback(() => {
        clearLocalSessionData();
        window.dispatchEvent(new CustomEvent(USER_SESSION_EXPIRED_EVENT));
        showNotification('error', USER_SESSION_EXPIRED_MESSAGE, 8000);
    }, [clearLocalSessionData]);

    const handleAdminSessionExpired = useCallback(() => {
        localStorage.removeItem('prismax_admin_token');
        window.dispatchEvent(new CustomEvent(ADMIN_SESSION_EXPIRED_EVENT));
        showNotification('error', ADMIN_SESSION_EXPIRED_MESSAGE, 8000);
    }, []);


    return (
        <SwapMainAppContext.Provider
            value={{
                isContextReady,
                notification,
                showNotification,
                walletAddress,
                setWalletAddress,
                linkedWalletChain,
                setLinkedWalletChain,
                selectedToken,
                setSelectedToken,
                amount,
                setAmount,
                usdtTokenAddress,
                usdcTokenAddress,
                prismaxRecevingAddress,
                prismaxEvmReceivingAddress,
                transactionTrigger,
                setTransactionTrigger,
                connectWalletModalRequest,
                connectWalletModalForceWalletList,
                requestConnectWalletModal,
                onMembershipPaymentSuccess,
                setOnMembershipPaymentSuccess,
                isQaReviewInProgress,
                setIsQaReviewInProgress,
                requestLeaveQaReviewConfirmation,
                setRequestLeaveQaReviewConfirmation,
                userClass,
                setUserClass,
                userRole,
                setUserRole,
                qaRole,
                setQaRole,
                userSettings,
                setUserSettings,
                userEmail,
                setUserEmail,
                isProcessing,
                setIsProcessing,
                fetchUserInfoAfterConnect,
                setUsdtBalance,
                usdtBalance,
                usdcBalance,
                setUsdcBalance,
                showFirstTimeModal,
                pointsRefreshTrigger,
                setPointsRefreshTrigger,
                gatewayToken,   //match to user
                setGatewayToken,
                setSessionUrlKey, //used to decrypt url to control robot
                sessionUrlKey,
                clearUserData,
                clearLocalSessionData,
                handleUserSessionExpired,
                handleAdminSessionExpired,
                shrinkLeftPanel,
                setShrinkLeftPanel,
                setShowPanel,
                showPanel,
                userId,
                setUserId,
                selectedChain,
                setSelectedChain,
                referralCode,
                setReferralCode,
                robotDataDict,
                robotSlugToRobotId,
                livePaused,
                videoId,
                globalLiveRobotId,
                isQueueAttentionActive,
                setIsQueueAttentionActive,
                shouldRefreshVLADataCache,
                setShouldRefreshVLADataCache
            }}
        >
            {children}
            <FirstTimeModal
                isOpen={showFirstTimeModal}
                onClose={() => setShowFirstTimeModal(false)}
                selectedChain={selectedChain}
            />
        </SwapMainAppContext.Provider>
    );
}
